import { useEffect, RefObject } from 'react';

interface UseIntersectionObserverProps<T extends Element> {
  target: RefObject<T | null>;
  onIntersect: () => void;
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

const callbackMap = new WeakMap<Element, () => void>();
const observerInstances = new Map<string, IntersectionObserver>();

const getSharedObserver = (
  threshold: number,
  rootMargin: string,
): IntersectionObserver => {
  const key = `${threshold}-${rootMargin}`;

  if (!observerInstances.has(key)) {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const callback = callbackMap.get(entry.target);
            if (callback) {
              callback();
            }
          }
        });
      },
      { threshold, rootMargin },
    );
    observerInstances.set(key, observer);
  }

  return observerInstances.get(key)!;
};

export const useIntersectionObserver = <T extends Element>({
  target,
  onIntersect,
  threshold = 0.1,
  rootMargin = '0px',
  triggerOnce = true,
}: UseIntersectionObserverProps<T>) => {
  useEffect(() => {
    const currentTarget = target.current;
    if (!currentTarget) return;

    const observer = getSharedObserver(threshold, rootMargin);

    callbackMap.set(currentTarget, () => {
      onIntersect();
      if (triggerOnce) {
        observer.unobserve(currentTarget);
        callbackMap.delete(currentTarget);
      }
    });

    observer.observe(currentTarget);

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
        callbackMap.delete(currentTarget);
      }
    };
  }, [target, onIntersect, threshold, rootMargin, triggerOnce]);
};
